import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    setIsAdScreen,
    setIsAnimationScreen,
    setMediaDelayDuration,
    setScreenDetails,
    updateCampaignDuration,
    updateCampaignMuted,
} from '../../../../../redux/Slices/layoutSlice';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLocation } from 'react-router-dom';

const InteractiveLayoutPreview = () => {
    const dispatch = useDispatch();
    const location = useLocation();

    const { screens_setting = [], layoutAspectRatio } = useSelector((state) => state.layout);
    const selectedScreen = useSelector((state) => state.layout.screenDetails);

    const [aspectWidth, setAspectWidth] = useState(0);
    const [aspectHeight, setAspectHeight] = useState(0);
    const [loading, setLoading] = useState(true);

    const isAnimatedLayout = location.pathname.startsWith('/apps/animated_layouts');

    // Keep aspect values in sync with store; control loading state
    useEffect(() => {
        if (layoutAspectRatio?.width && layoutAspectRatio?.height) {
            setAspectWidth(layoutAspectRatio.width);
            setAspectHeight(layoutAspectRatio.height);
            setLoading(false);
        } else {
            setAspectWidth(0);
            setAspectHeight(0);
            setLoading(false); // still render container even if unknown
        }
    }, [layoutAspectRatio]);

    const handleChangeScreen = (screen) => {
        if (!screen) return;

        const live = screen.settings?.live ?? {};
        dispatch(setScreenDetails(screen));
        dispatch(setIsAdScreen(!!live.is_Ad_Screen_Enabled));
        dispatch(setMediaDelayDuration(live.media_delay_duration ?? 0));

        // Only animation/muted/duration for base screens (no parent)
        const hasParent = Array.isArray(screen?.parent_screen) && screen.parent_screen.length > 0;
        if (!hasParent) {
            dispatch(setIsAnimationScreen(!!live.is_Animate_Screen_Enabled));
            dispatch(updateCampaignDuration(live.campaign_duration ?? 0));
            dispatch(updateCampaignMuted(!!live.is_muted));
        }
    };

    const { scaledWidth, scaledHeight } = useMemo(() => {
        if (!aspectWidth || !aspectHeight) return { scaledWidth: 289, scaledHeight: 350 };
        const widthRatio = 289 / aspectWidth;
        const heightRatio = 350 / aspectHeight;
        const scale = Math.min(widthRatio, heightRatio);
        return {
            scaledWidth: Math.max(1, aspectWidth * scale),
            scaledHeight: Math.max(1, aspectHeight * scale),
        };
    }, [aspectWidth, aspectHeight]);


    const sortByLiveOrder = (a, b) => {
        const ao = Number(a?.settings?.live?.order);
        const bo = Number(b?.settings?.live?.order);
        const aHas = Number.isFinite(ao);
        const bHas = Number.isFinite(bo);
        
        if (aHas && bHas) {
             const diff = ao - bo; // Ascending order
             if (diff !== 0) return diff;
        }
        
        // Fallback to ID sorting
        return (a.id || 0) - (b.id || 0);
    };

    // choose the source list depending on animation mode
    const baseList = isAnimatedLayout
        ? screens_setting
        : [...screens_setting].sort(sortByLiveOrder);

    return (
        <>
            {loading ? (
                <div className="w-100 h-100 d-flex align-items-center">
                    <ProgressSpinner
                        style={{ width: '60px', height: '60px', opacity: 0.1 }}
                        strokeWidth="3"
                        fill="var(--surface-ground)"
                        animationDuration="1s"
                    />
                </div>
            ) : (
                <div className="place-items-center w-full mb-1 text-center mt-3 h-auto">
                    <div className="flex relative justify-center w-[289px] h-100">
                        <div
                            className="p-4 relative border border-gray-300 rounded-md bg-white"
                            style={{
                                backgroundColor: '#F6F6F6',
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25' viewBox='0 0 1600 1200'%3E%3Cdefs%3E%3ClinearGradient id='a' x1='0' x2='0' y1='0' y2='100%25' gradientTransform='rotate(128,927,463)' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23eeeff2'/%3E%3Cstop offset='0.02' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.02' stop-color='%23e6e8eb'/%3E%3Cstop offset='0.032' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.032' stop-color='%23f2f2f2'/%3E%3Cstop offset='0.056' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.056' stop-color='%23efeff1'/%3E%3Cstop offset='0.07' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.07' stop-color='%23f0f1f1'/%3E%3Cstop offset='0.1' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.1' stop-color='%23f2f2f3'/%3E%3Cstop offset='0.126' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.126' stop-color='%23e2e6f2'/%3E%3Cstop offset='0.142' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.142' stop-color='%23f2f2f2'/%3E%3Cstop offset='0.159' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.159' stop-color='%23f1f1f2'/%3E%3Cstop offset='0.17' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.17' stop-color='%23f0f1f2'/%3E%3Cstop offset='0.197' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.197' stop-color='%23eeeef1'/%3E%3Cstop offset='0.218' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.218' stop-color='%23e9ecf4'/%3E%3Cstop offset='0.239' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.239' stop-color='%23eff0f3'/%3E%3Cstop offset='0.254' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.254' stop-color='%23f2f2f3'/%3E%3Cstop offset='0.283' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.283' stop-color='%23f0f0f2'/%3E%3Cstop offset='0.294' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.294' stop-color='%23e9ecf4'/%3E%3Cstop offset='0.305' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.305' stop-color='%23f3f3f3'/%3E%3Cstop offset='0.332' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.332' stop-color='%23e6e8ed'/%3E%3Cstop offset='0.346' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.346' stop-color='%23ebebed'/%3E%3Cstop offset='0.362' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.362' stop-color='%23f1f1f2'/%3E%3Cstop offset='0.381' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.381' stop-color='%23eeeeee'/%3E%3Cstop offset='0.415' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.415' stop-color='%23f0f1f1'/%3E%3Cstop offset='0.428' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.428' stop-color='%23e6eaf3'/%3E%3Cstop offset='0.442' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.442' stop-color='%23e2e3e7'/%3E%3Cstop offset='0.456' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.456' stop-color='%23f3f3f3'/%3E%3Cstop offset='0.498' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.498' stop-color='%23ebecef'/%3E%3Cstop offset='0.511' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.511' stop-color='%23eaeaeb'/%3E%3Cstop offset='0.532' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.532' stop-color='%23f2f2f3'/%3E%3Cstop offset='0.541' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.541' stop-color='%23f2f2f2'/%3E%3Cstop offset='0.56' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.56' stop-color='%23e3e6ee'/%3E%3Cstop offset='0.581' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.581' stop-color='%23f0f0f1'/%3E%3Cstop offset='0.6' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.6' stop-color='%23edeef2'/%3E%3Cstop offset='0.618' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.618' stop-color='%23f1f2f3'/%3E%3Cstop offset='0.656' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.656' stop-color='%23e4e8f1'/%3E%3Cstop offset='0.679' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.679' stop-color='%23e1e5f2'/%3E%3Cstop offset='0.689' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.689' stop-color='%23eff0f3'/%3E%3Cstop offset='0.720' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.720' stop-color='%23edeef0'/%3E%3Cstop offset='0.734' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.734' stop-color='%23f1f1f1'/%3E%3Cstop offset='0.748' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.748' stop-color='%23e1e1e2'/%3E%3Cstop offset='0.764' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.764' stop-color='%23e4e4e5'/%3E%3Cstop offset='0.788' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.788' stop-color='%23e4e6eb'/%3E%3Cstop offset='0.808' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.808' stop-color='%23e3e7ef'/%3E%3Cstop offset='0.831' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.831' stop-color='%23e7e9ef'/%3E%3Cstop offset='0.856' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.856' stop-color='%23f2f2f2'/%3E%3Cstop offset='0.872' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.872' stop-color='%23edeff3'/%3E%3Cstop offset='0.894' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.894' stop-color='%23edeef1'/%3E%3Cstop offset='0.914' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.914' stop-color='%23eaedf4'/%3E%3Cstop offset='0.942' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.942' stop-color='%23edeef3'/%3E%3Cstop offset='0.957' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.957' stop-color='%23e4e7ed'/%3E%3Cstop offset='0.973' stop-color='%23F3F3F3'/%3E%3Cstop offset='0.973' stop-color='%23e0e6f5'/%3E%3Cstop offset='1' stop-color='%23F3F3F3'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23a)' x='0' y='0' width='100%25' height='100%25'/%3E%3C/svg%3E")`,
                                backgroundAttachment: 'fixed',
                                backgroundSize: 'cover',
                                width: scaledWidth,
                                height: scaledHeight,
                            }}
                        >
                            {baseList.map((area, index) => {
                                // Build the render list for this area
                                let areaList = [area];

                                // Animated branch (unchanged)
                                if (
                                    isAnimatedLayout &&
                                    area?.settings?.live?.is_Animate_Screen_Enabled &&
                                    Array.isArray(area?.related_screen) &&
                                    area.related_screen.length > 0
                                ) {
                                    areaList = [area, ...area.related_screen];
                                }

                                if (isAnimatedLayout) {
                                    const sorted = [...areaList].sort(sortByLiveOrder);

                                    return sorted.map((areaR, i) => {
                                        const live = areaR?.settings?.live ?? {};
                                        const key = `${areaR?.id ?? areaR?.name ?? 'area'}-${i}`;
                                        const left = live.is_Animate_Screen_Enabled ? live.Animated_start_margin : areaR?.start_margin;
                                        const top = live.is_Animate_Screen_Enabled ? live.Animated_top_margin : areaR?.top_margin;
                                        const width = live.is_Animate_Screen_Enabled ? live.Animated_Width : areaR?.width;
                                        const height = live.is_Animate_Screen_Enabled ? live.Animated_Height : areaR?.height;
                                        const zIndex = 100 - (Number(live.order) || 0);

                                        return (
                                            <div
                                                key={key}
                                                className={`flex justify-center text-[black] items-center absolute border border-black text-center text-sm ${areaR?.name === selectedScreen?.name ? 'bg-[#deddff]' : 'bg-white/50'}`}
                                                style={{
                                                    left: `${left ?? 0}%`,
                                                    top: `${top ?? 0}%`,
                                                    width: `${width ?? 0}%`,
                                                    height: `${height ?? 0}%`,
                                                    zIndex: zIndex
                                                }}
                                                onClick={() => handleChangeScreen(areaR)}
                                            >
                                                {areaR.name}
                                            </div>
                                        );
                                    });
                                }

                                // Non-animated branch: display area ID
                                const key = `${area?.id ?? area?.name ?? 'area'}-${index}`;
                                const label = index + 1;
                                const zIndex = 100 - (Number(area?.settings?.live?.order) || 0);

                                return (
                                    <div
                                        key={key}
                                        className={`flex justify-center text-[black] items-center absolute border border-black text-center text-sm ${area?.name === selectedScreen?.name ? 'bg-[#deddff]' : 'bg-white/50'}`}
                                        style={{
                                            left: `${area?.start_margin ?? 0}%`,
                                            top: `${area?.top_margin ?? 0}%`,
                                            width: `${area?.width ?? 0}%`,
                                            height: `${area?.height ?? 0}%`,
                                            zIndex: zIndex
                                        }}
                                        onClick={() => handleChangeScreen(area)}
                                    >
                                        {label}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default InteractiveLayoutPreview;
